import forOwn from 'lodash/forOwn';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';
const DEFAULT_REGVAR_METADATA_VALIDATIONS = {
    boolean () {
        return {
            boolean: true
        };
    },
    datetime (regvarMetadata) {
        return {
            [regvarMetadata.includeTime ? 'isoDateWithTime' : 'isoDate']: true,
            beforeToday: true,
            birthDate: true,
            deathDate: true
        };
    },
    decimal (regvarMetadata) {
        const rvm = regvarMetadata;
        return {
            float: true,
            min: isNil(rvm.min) ? undefined : rvm.min.toString().replace(',', '.'),
            max: isNil(rvm.max) ? undefined : rvm.max.toString().replace(',', '.')
        };
    },
    integer (regvarMetadata) {
        const rvm = regvarMetadata;
        return {
            integer: true,
            min: isNil(rvm.min) ? undefined : rvm.min,
            max: isNil(rvm.max) ? undefined : rvm.max
        };
    },
    string (regvarMetadata) {
        const rvm = regvarMetadata;
        return {
            maxLength: rvm.maxLength,
            validCharacters: rvm.validCharacters || undefined
        };
    },
    list () {
        return {
            integer: true,
            min: 0
        };
    },
    vd () {
        return {
            integer: true
        };
    }
};
const getDefaultRegvarMetadataValidations = (regvarMetadata)=>{
    const fn = DEFAULT_REGVAR_METADATA_VALIDATIONS[regvarMetadata.dataType];
    const rules = fn ? fn(regvarMetadata) : {};
    return Object.keys(rules).reduce((reduction, ruleName)=>{
        reduction[ruleName] = {
            isFatal: true,
            value: rules[ruleName]
        };
        return reduction;
    }, {});
};
const getCustomRules = (props, validator)=>{
    const rules = [];
    forOwn(props, (_value, key)=>{
        if (validator.hasMethod(key)) {
            rules.push(key);
        }
    });
    return rules;
};
export const stripRules = (rules, whitelist, blacklist)=>{
    if (whitelist) {
        return pick(rules, [
            ...whitelist,
            'messages'
        ]);
    }
    if (blacklist) {
        return omit(rules, blacklist);
    }
    return rules;
};
export const getValidationRules = (props, regvarMetadata, options)=>{
    const regvarMetadataRules = getDefaultRegvarMetadataValidations(regvarMetadata);
    const customRules = getCustomRules(props, options.validator).reduce((reduction, ruleName)=>{
        const customMessage = props[`${ruleName}Message`];
        const ruleType = props[`${ruleName}Type`];
        reduction[ruleName] = {
            isFatal: ruleType !== 'warning',
            value: props[ruleName]
        };
        if (customMessage) {
            reduction[ruleName].message = customMessage;
        }
        return reduction;
    }, {});
    const allRules = Object.entries({
        ...regvarMetadataRules,
        ...customRules
    }).reduce((prev, entry)=>{
        const [ruleName, ruleData] = entry;
        // Ta bara med valideringsregler som validatorn vet om.
        if (options.validator.hasMethod(ruleName)) {
            prev[ruleName] = ruleData;
        }
        return prev;
    }, {});
    return stripRules(allRules, options?.whitelist, options?.blacklist);
};
/**
 * Returns an array of prop names related to validation props.
 *
 * @returns {Array}
 */ export const getValidationPropNames = (validRules = [], options)=>{
    return Object.keys(options.validator.methods).reduce((reduction, propName)=>{
        if (validRules.indexOf(propName) === -1) {
            reduction.push(propName);
        }
        reduction.push(`${propName}Message`);
        reduction.push(`${propName}Type`);
        return reduction;
    }, []);
};
/**
 * Validates a value based on the regvarMetadata and its props. Validation props are automatically extracted.
 * Options takes { whitelist, blacklist } and can include an array of whitelisted or blacklisted validation rules.
 *
 * @returns {Array} - Validation result.
 */ export const validate = (value, regvarMetadata, props, options)=>{
    const rules = getValidationRules(props, regvarMetadata, options);
    return options.validator.validate(value, rules);
};
